import { ServiceCode } from '@/shared/types';

import { config } from '@/config/index';

import { parseBool } from '@/helpers/parseBool';

import { SERVICE_CODES } from '@/lib/consts';

import { UserService } from '@/api/types';

declare global {
  interface Grecaptcha {
    execute(siteKey: string, options: { action: string }): Promise<string>;

    ready(cb: () => void): void;
  }

  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

interface CachedToken {
  token: string;
  timestamp: number;
}

interface CaptchaEndpointConfig {
  path: RegExp;
  action: string;
  serviceCode?: ServiceCode;
}

export class CaptchaManager {
  private servicesMap: Map<ServiceCode, boolean> = new Map();
  private isGloballyEnabled: boolean = false;
  private tokenCache: Map<string, CachedToken> = new Map();
  private readonly TOKEN_CACHE_TTL = 90000;

  private readonly captchaEndpoints: CaptchaEndpointConfig[] = [
    {
      path: /^\/user-requests\/[^/]+\/search(?:\?.*)?$/,
      action: 'search',
    },
    {
      path: /^\/user-requests\/[^/]+\/submitForm$/,
      action: 'submit_form',
    },
    {
      path: /^\/user-requests\/[^/]+\/(create-order|generate-otp|validate-otp)$/,
      action: 'service_actions',
    },
    {
      path: /^\/user-requests\/[^/]+\/canSubmit$/,
      action: 'can_submit',
    },
    {
      path: /^\/weapons\/extraction\/?$/,
      action: 'erz_search',
    },
    {
      path: /^\/social-protect\/(add-topic|add-order|add-message|send-feedback)$/,
      action: 'social_protect_actions',
      serviceCode: SERVICE_CODES.SOCIAL_PROTECT,
    },
  ];

  constructor() {
    this.isGloballyEnabled = parseBool(import.meta.env.VITE_CAPTCHA_ENABLED || 'false');
  }

  setServices(services: UserService[]): void {
    this.servicesMap.clear();
    services.forEach((service) => {
      this.servicesMap.set(service.code, service.captcha_is_enabled);
    });
  }

  isRequired(serviceCode?: ServiceCode): boolean {
    if (!this.isGloballyEnabled) {
      return false;
    }

    if (!serviceCode) {
      return true;
    }

    return this.servicesMap.get(serviceCode) ?? true;
  }

  async getToken(action: string, serviceCode?: ServiceCode): Promise<string | null> {
    if (!this.isRequired(serviceCode)) {
      return null;
    }

    if (typeof window === 'undefined' || !window.grecaptcha) {
      return null;
    }

    const cacheKey = this.getCacheKey(action, serviceCode);
    const cachedToken = this.getCachedToken(cacheKey);

    // TODO: keep caching token #MVSUSLUH-5019
    if (false) {
      return cachedToken;
    }

    try {
      const token = await new Promise<string>((resolve) => {
        window.grecaptcha!.ready(async () => {
          const captchaToken = await window.grecaptcha!.execute(config.captcha.siteKey, { action });
          resolve(captchaToken);
        });
      });

      this.setCachedToken(cacheKey, token);

      return token;
    } catch (error) {
      console.error('Captcha token generation failed:', error);
      return null;
    }
  }

  private getCacheKey(action: string, serviceCode?: ServiceCode): string {
    return `${action}_${serviceCode || 'global'}`;
  }

  private getCachedToken(cacheKey: string): string | null {
    const cached = this.tokenCache.get(cacheKey);

    if (!cached) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - cached.timestamp > this.TOKEN_CACHE_TTL;

    if (isExpired) {
      this.tokenCache.delete(cacheKey);
      return null;
    }

    return cached.token;
  }

  private setCachedToken(cacheKey: string, token: string): void {
    this.tokenCache.set(cacheKey, {
      token,
      timestamp: Date.now(),
    });
  }

  private matchCaptchaEndpoint(url: string): CaptchaEndpointConfig | null {
    for (const endpointConfig of this.captchaEndpoints) {
      if (endpointConfig.path.test(url)) {
        return endpointConfig;
      }
    }
    return null;
  }

  private extractServiceCodeFromUrl(url: string): ServiceCode | undefined {
    const userRequestsMatch = url.match(/^\/user-requests\/([^/]+)\//);
    if (userRequestsMatch) {
      return userRequestsMatch[1] as ServiceCode;
    }
    return undefined;
  }

  private getServiceCodeForEndpoint(url: string, endpointConfig: CaptchaEndpointConfig): ServiceCode | undefined {
    if (endpointConfig.serviceCode) {
      return endpointConfig.serviceCode;
    }
    return this.extractServiceCodeFromUrl(url);
  }

  async getTokenForRequest(url: string): Promise<string | null> {
    const endpointConfig = this.matchCaptchaEndpoint(url);

    if (!endpointConfig) {
      return null;
    }

    const serviceCode = this.getServiceCodeForEndpoint(url, endpointConfig);

    if (!this.isRequired(serviceCode)) {
      return null;
    }

    return this.getToken(endpointConfig.action, serviceCode);
  }

  clear(): void {
    this.servicesMap.clear();
    this.tokenCache.clear();
  }
}

export const captchaManager = new CaptchaManager();
